import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from '../makecodeArcadePageStyles';
import Button from '../../../components/ui/Button';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import {
  explainPaperStyle,
  explainPaperTitleStyle,
  explainPaperTextStyle,
  explainExamplePaperStyle,
  explainExampleImageBoxStyle,
  codeBackgroundStyle
} from '../../../styles/commonStyles';

const IMG = (file: string) => `/images/makecodearcade/explains/Scroll/${file}`;

const ScreenScrollExplain: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useLayoutContext();

  return (
    <div style={getMakecodeArcadePageRootStyle(isMobile)}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
        <Button
          variant="line"
          leftIcon={<LeftArrowIcon width={20} height={20} />}
          onClick={() => navigate('/makecodearcade/explains/')}
          style={{ marginRight: '16px' }}
        >
          돌아가기
        </Button>
        <h1 style={{ margin: 0, fontSize: '24px', fontFamily: 'var(--font-family-primary)' }}>
          화면 스크롤 설정하기
        </h1>
      </div>

      <div
        style={{
          marginBottom: '24px',
          border: '1px solid #ccc',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <h2 style={{ fontWeight: 700, marginBottom: '16px', fontSize: '18px' }}>📚 목차</h2>
        <ul style={{ listStyleType: 'none', padding: 0 }}>
          <li style={{ marginBottom: '8px' }}>
            <a
              href="#screen-scroll-소개"
              style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}
            >
              화면 스크롤이란?
            </a>
          </li>
          <li style={{ marginBottom: '8px' }}>
            <a
              href="#screen-scroll-기능-추가하기"
              style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}
            >
              Background Scroll 확장 추가하기
            </a>
          </li>
          <li style={{ marginBottom: '8px' }}>
            <a
              href="#screen-scroll-블록-알아보기"
              style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}
            >
              Scroller 블록 알아보기
            </a>
          </li>
          <ul style={{ listStyleType: 'circle', paddingLeft: '20px', marginTop: '6px' }}>
            <li style={{ marginBottom: '3px' }}>
              <a href="#screen-scroll-배경-스크롤" style={{ textDecoration: 'none', color: '#555' }}>
                배경 이미지 스크롤·반복
              </a>
            </li>
            <li style={{ marginBottom: '3px' }}>
              <a href="#screen-scroll-패럴랙스" style={{ textDecoration: 'none', color: '#555' }}>
                패럴랙스(여러 레이어)
              </a>
            </li>
            <li style={{ marginBottom: '3px' }}>
              <a href="#screen-scroll-카메라" style={{ textDecoration: 'none', color: '#555' }}>
                카메라 따라가기 (기본 블록)
              </a>
            </li>
          </ul>
        </ul>
      </div>

      <div
        id="screen-scroll-소개"
        style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' }}
      >
        <img
          src={IMG('Scroll01.png')}
          alt="스크롤 배경이 있는 게임 예시"
          style={{ width: isMobile ? '100%' : '28%', maxWidth: 280, height: 'auto', borderRadius: '8px' }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        <p style={{ margin: 0, flex: 1, minWidth: 240, color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
          횡스크롤·런 게임처럼 <b>화면보다 넓은 맵</b>을 만들 때, 배경과 오브젝트가 움직이며 따라오는 느낌을 주려면{' '}
          <b>화면 스크롤</b> 설정이 필요합니다.<br />
          MakeCode Arcade에서는 Microsoft의 <b>arcade-background-scroll</b> 확장(도구 상자 이름:{' '}
          <b>Scroller</b>)으로 배경을 스크롤·반복하고, 여러 레이어를 다른 속도로 움직여{' '}
          <b>패럴랙스</b> 효과를 낼 수 있습니다.<br />
          캐릭터가 맵 안을 돌아다니게 하려면 Scroller와 함께 <b>장면(Scene)</b> 도구 상자의{' '}
          <b>카메라 따라가기</b> 블록을 같이 쓰는 경우가 많습니다.
        </p>
      </div>

      <h2
        style={{ fontWeight: 700, marginBottom: '8px', fontSize: '18px' }}
        id="screen-scroll-기능-추가하기"
      >
        Background Scroll 확장 추가하기
      </h2>
      <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' }}>
        <img
          src={IMG('Scroll02.png')}
          alt="확장 검색 화면"
          style={{ width: isMobile ? '100%' : '22%', maxWidth: 220, height: 'auto', borderRadius: '8px' }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        <p style={{ margin: 0, flex: 1, minWidth: 240, color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
          <b>arcade-background-scroll</b>는 배경 이미지를 스크롤·루프하는 확장입니다.<br />
          에디터에서 <b>⚙️ 설정 → 확장(Extensions)</b>을 연 뒤, 검색창에{' '}
          <code style={codeBackgroundStyle}>arcade-background-scroll</code> 또는{' '}
          <code style={codeBackgroundStyle}>background scroll</code>을 입력해 추가합니다.<br />
          GitHub 주소로 직접 넣을 때:{' '}
          <code style={codeBackgroundStyle}>https://github.com/microsoft/arcade-background-scroll</code>
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' }}>
        <img
          src={IMG('Scroll03.png')}
          alt="Scroller 도구상자"
          style={{ width: isMobile ? '100%' : '22%', maxWidth: 220, height: 'auto' }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        <p style={{ margin: 0, flex: 1, minWidth: 240, color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
          추가가 완료되면 도구 상자에 <b>Scroller</b> 카테고리가 생깁니다. 배경 스프라이트·타일맵을
          준비한 뒤, 아래 블록들로 스크롤 속도와 방향을 조절합니다.
        </p>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: '24px',
          gap: '16px',
          flexWrap: 'wrap'
        }}
      >
        <img
          src={IMG('Scroll04.png')}
          alt="Scroller 블록 모음"
          style={{ width: isMobile ? '100%' : '48%', height: 'auto' }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        <img
          src={IMG('Scroll05.png')}
          alt="Scroller 블록 상세"
          style={{ width: isMobile ? '100%' : '48%', height: 'auto' }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>

      <h2
        style={{ fontWeight: 700, marginBottom: '8px', fontSize: '18px' }}
        id="screen-scroll-블록-알아보기"
      >
        Scroller 블록 알아보기
      </h2>
      <p style={{ marginBottom: '16px', color: 'var(--Color-text-normal)' }}>
        Scroller 도구 상자 블록을 용도별로 정리했습니다. 에디터 버전에 따라 블록 문구는 조금 다를 수
        있습니다.
      </p>

      <div style={explainPaperStyle} id="screen-scroll-배경-스크롤">
        <h3 style={explainPaperTitleStyle}>1. 배경 이미지 스크롤·반복</h3>
        <p style={explainPaperTextStyle}>
          넓은 배경 이미지(또는 타일)를 화면 밖까지 이어 붙이고, 일정 속도로 옆(또는 위아래)으로
          움직이게 합니다. 끝에 도달하면 처음과 이어지도록 <b>반복(loop)</b>되게 설정할 수 있습니다.
        </p>
        <ul style={{ margin: '0 0 16px', paddingLeft: 20, lineHeight: 1.6, color: 'var(--Color-text-normal)' }}>
          <li>게임 시작 시 배경 스크롤을 켜는 블록을 <b>시작</b>에 둡니다.</li>
          <li>스크롤 속도는 픽셀/프레임 단위로 조절합니다. 값이 클수록 빠르게 움직입니다.</li>
          <li>배경 이미지 가로 길이는 화면보다 길게 제작하는 것이 자연스럽습니다.</li>
        </ul>
        <div style={explainExamplePaperStyle}>
          <div style={explainExampleImageBoxStyle}>
            <img
              src={IMG('Scroll06.png')}
              alt="배경 스크롤 블록 예시"
              style={{ width: '100%', height: 'auto' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        </div>
      </div>

      <div style={explainPaperStyle} id="screen-scroll-패럴랙스">
        <h3 style={explainPaperTitleStyle}>2. 패럴랙스(여러 레이어)</h3>
        <p style={explainPaperTextStyle}>
          하늘·산·숲처럼 <b>레이어를 나눠</b> 각각 다른 속도로 스크롤하면 원근감 있는 패럴랙스가
          됩니다. 앞쪽 레이어는 빠르게, 먼 레이어는 느리게 움직이게 설정해 보세요.
        </p>
        <ul style={{ margin: '0 0 16px', paddingLeft: 20, lineHeight: 1.6, color: 'var(--Color-text-normal)' }}>
          <li>레이어마다 배경 스프라이트(또는 이미지)를 따로 만듭니다.</li>
          <li>각 레이어에 서로 다른 스크롤 속도를 지정합니다.</li>
          <li>플레이어·지형은 스크롤 배경 위에 별도 스프라이트로 올립니다.</li>
        </ul>
        <div style={explainExamplePaperStyle}>
          <div style={explainExampleImageBoxStyle}>
            <img
              src={IMG('Scroll07.png')}
              alt="패럴랙스 레이어 예시"
              style={{ width: '100%', height: 'auto' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        </div>
      </div>

      <div style={explainPaperStyle} id="screen-scroll-카메라">
        <h3 style={explainPaperTitleStyle}>3. 카메라 따라가기 (기본 블록)</h3>
        <p style={explainPaperTextStyle}>
          배경만 스크롤하는 것과 별도로, <b>플레이어가 맵 안을 이동</b>할 때 화면이 캐릭터를 따라가게
          하려면 <b>장면(Scene)</b> 도구 상자의 카메라 블록을 사용합니다. Scroller로 움직이는 배경과
          조합하면 횡스크롤 게임을 만들기 쉽습니다.
        </p>
        <div
          style={{
            ...codeBackgroundStyle,
            marginBottom: 16,
            fontFamily: 'monospace',
            fontSize: 13
          }}
        >
          예: 시작 → [플레이어 스프라이트] 생성 → 카메라가 [플레이어]를 따라가게 하기
        </div>
        <p style={{ ...explainPaperTextStyle, marginBottom: 0 }}>
          <b>팁:</b> 배경 스크롤 속도와 캐릭터 이동 속도가 맞지 않으면 어지러울 수 있습니다. 먼저
          캐릭터 이동·카메라를 맞춘 뒤 배경 스크롤 속도를 조절해 보세요.
        </p>
      </div>
    </div>
  );
};

export default ScreenScrollExplain;
